/**
 * SPARC-009: Shared geographic zone context for Government Data dashboard.
 * Scopes Opportunities, Industry News, and Data Integrations tabs.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useRef,
  ReactNode,
  Dispatch,
  SetStateAction,
} from 'react';
import { useAuthContext, useOptionalConfig, useWorkspace } from '@tracekit/core';
import { useGeographicZoneOpportunityCounts } from '../hooks/useGeographicZoneOpportunityCounts';
import {
  decodeCountryAccessFromToken,
  getAccessibleZones,
  getDefaultZone,
  getGeographicZoneFilterValue,
  getZonesWithOpportunities,
  GEOGRAPHIC_ZONES,
  isZoneAccessible,
  readStoredGeographicZone,
  resolveAllAccessDefaultZone,
  resolveInitialGeographicZone,
  WORLD_ZONE,
  type GeographicZone,
  type GeographicZoneId,
  type GeographicZoneOpportunityCounts,
} from '../utils/countryAccess';

const STORAGE_KEY = 'gov-data-geographic-zone';

export interface GeographicZoneState {
  selectedGeographicZone: GeographicZoneId;
  setSelectedGeographicZone: Dispatch<SetStateAction<GeographicZoneId>>;
  /** False until JWT country_access has been decoded and the stored zone restored. */
  isGeographicZoneReady: boolean;
  accessibleZones: GeographicZone[];
  zonesWithOpportunities: GeographicZone[];
  zoneOpportunityCounts: GeographicZoneOpportunityCounts | undefined;
  zoneCountsLoaded: boolean;
  userCountryAccess: string[];
  zoneFilterValue: string | undefined;
}

const GeographicZoneContext = createContext<GeographicZoneState | undefined>(undefined);

interface GeographicZoneProviderProps {
  children: ReactNode;
}

export function GeographicZoneProvider({ children }: GeographicZoneProviderProps) {
  const { accessToken } = useAuthContext();
  const { projectId } = useWorkspace();
  const config = useOptionalConfig();
  const hasResolvedAccessDefault = useRef(false);

  const allAccessDefault = resolveAllAccessDefaultZone(
    config?.customization?.defaultGeographicZone
  );
  const zoneOptions = useMemo(
    () => ({ allAccessDefault }),
    [allAccessDefault]
  );

  const userCountryAccess = useMemo(
    () => decodeCountryAccessFromToken(accessToken),
    [accessToken]
  );

  const accessibleZones = useMemo(
    () => getAccessibleZones(userCountryAccess),
    [userCountryAccess]
  );

  const [isGeographicZoneReady, setIsGeographicZoneReady] = useState(false);

  const { data: zoneOpportunityCounts, isSuccess: zoneCountsLoaded } =
    useGeographicZoneOpportunityCounts(projectId, { enabled: isGeographicZoneReady });

  const zonesWithOpportunities = useMemo(
    () =>
      getZonesWithOpportunities(userCountryAccess, zoneOpportunityCounts, {
        countsLoaded: zoneCountsLoaded,
      }),
    [userCountryAccess, zoneOpportunityCounts, zoneCountsLoaded]
  );

  const [selectedGeographicZone, setSelectedGeographicZoneState] =
    useState<GeographicZoneId>(
      () => readStoredGeographicZone() ?? allAccessDefault
    );

  const setSelectedGeographicZone: Dispatch<SetStateAction<GeographicZoneId>> = (
    value
  ) => {
    setSelectedGeographicZoneState(value);
  };

  // SPARC ALL lands on Canada (config); stored zone wins after the first choice.
  // Wait for accessToken so country_access is stable before the first zone-scoped API fetch.
  useEffect(() => {
    if (!accessToken) return;

    if (!hasResolvedAccessDefault.current) {
      hasResolvedAccessDefault.current = true;
      setSelectedGeographicZoneState(
        resolveInitialGeographicZone(
          readStoredGeographicZone(),
          userCountryAccess,
          zoneOptions
        )
      );
      setIsGeographicZoneReady(true);
      return;
    }

    setSelectedGeographicZoneState((current) => {
      const zoneDef =
        current === 'world'
          ? WORLD_ZONE
          : GEOGRAPHIC_ZONES.find((zone) => zone.id === current) ?? WORLD_ZONE;
      return isZoneAccessible(zoneDef, userCountryAccess)
        ? current
        : getDefaultZone(userCountryAccess, zoneOptions);
    });
  }, [accessToken, userCountryAccess, zoneOptions]);

  useEffect(() => {
    if (!isGeographicZoneReady) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedGeographicZone));
  }, [selectedGeographicZone, isGeographicZoneReady]);

  const zoneFilterValue = getGeographicZoneFilterValue(selectedGeographicZone);

  const value = useMemo<GeographicZoneState>(
    () => ({
      selectedGeographicZone,
      setSelectedGeographicZone,
      isGeographicZoneReady,
      accessibleZones,
      zonesWithOpportunities,
      zoneOpportunityCounts,
      zoneCountsLoaded,
      userCountryAccess,
      zoneFilterValue,
    }),
    [
      selectedGeographicZone,
      isGeographicZoneReady,
      accessibleZones,
      zonesWithOpportunities,
      zoneOpportunityCounts,
      zoneCountsLoaded,
      userCountryAccess,
      zoneFilterValue,
    ]
  );

  return (
    <GeographicZoneContext.Provider value={value}>
      {children}
    </GeographicZoneContext.Provider>
  );
}

export function useGeographicZone(): GeographicZoneState {
  const context = useContext(GeographicZoneContext);
  if (!context) {
    throw new Error('useGeographicZone must be used within GeographicZoneProvider');
  }
  return context;
}
